class ImageSlider extends HTMLElement {
  #autoplayTimer;
  #scrollFrame;
  #scrollEndTimer;
  #abortController;

  connectedCallback() {
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    this.viewport = this.querySelector('[data-image-slider-viewport]');
    this.slides = [...this.querySelectorAll('[data-image-slider-slide]')];
    this.dots = [...this.querySelectorAll('[data-image-slider-dot]')];
    this.previousButton = this.querySelector('[data-image-slider-previous]');
    this.nextButton = this.querySelector('[data-image-slider-next]');
    this.dialog = this.querySelector('[data-image-slider-dialog]');
    this.dialogImage = this.querySelector('[data-image-slider-dialog-image]');

    if (!this.viewport || this.slides.length === 0) return;

    this.#createLoopClones();

    const { signal } = this.#abortController;

    this.viewport.addEventListener('scroll', this.#handleScroll, { passive: true, signal });
    this.previousButton?.addEventListener('click', () => this.#move(-1), { signal });
    this.nextButton?.addEventListener('click', () => this.#move(1), { signal });

    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', () => this.#scrollTo(index), { signal });
    });

    this.querySelectorAll('[data-image-slider-open]').forEach((button) => {
      button.addEventListener('click', () => this.#openDialog(button), { signal });
    });

    this.querySelector('[data-image-slider-close]')?.addEventListener('click', this.#closeDialog, { signal });
    this.dialog?.addEventListener('click', this.#handleDialogClick, { signal });
    this.dialog?.addEventListener('close', this.#clearDialogImage, { signal });

    this.addEventListener('mouseenter', this.#stopAutoplay, { signal });
    this.addEventListener('mouseleave', this.#startAutoplay, { signal });
    this.addEventListener('focusin', this.#stopAutoplay, { signal });
    this.addEventListener('focusout', this.#handleFocusOut, { signal });
    window.addEventListener('resize', this.#handleResize, { signal });
    document.addEventListener('visibilitychange', this.#handleVisibilityChange, { signal });

    this.#updateControls(0);
    requestAnimationFrame(() => {
      this.#scrollTo(0, 'instant');
    });
    this.#startAutoplay();
  }

  disconnectedCallback() {
    this.#stopAutoplay();
    this.#abortController?.abort();
    cancelAnimationFrame(this.#scrollFrame);
    window.clearTimeout(this.#scrollEndTimer);
  }

  #isDesktop = () => window.matchMedia('(min-width: 750px)').matches;

  #scrollTo(index, behavior = 'smooth') {
    const slide = this.slides[index];
    if (!slide) return;

    this.#scrollToElement(slide, behavior);
  }

  #scrollToElement(slide, behavior = 'smooth') {
    if (!slide) return;
    this.viewport.scrollTo({ left: slide.offsetLeft, behavior });
  }

  #move(direction) {
    const isDesktop = this.#isDesktop();
    const columns = isDesktop ? Number(this.dataset.columns) || 1 : 1;
    const maxStartIndex = Math.max(this.slides.length - columns, 0);
    const currentIndex = Math.min(this.#getClosestSlideIndex(), maxStartIndex);

    if (maxStartIndex > 0) {
      if (direction < 0 && currentIndex === 0) {
        const cloneSelector = isDesktop
          ? '[data-image-slider-desktop-clone="last"]'
          : '[data-image-slider-mobile-clone="last"]';
        this.#scrollToElement(this.querySelector(cloneSelector));
        return;
      }

      if (direction > 0 && currentIndex === maxStartIndex) {
        const cloneSelector = isDesktop
          ? '[data-image-slider-desktop-clone="first"]'
          : '[data-image-slider-mobile-clone="first"]';
        this.#scrollToElement(this.querySelector(cloneSelector));
        return;
      }
    }

    const nextIndex = Math.min(Math.max(currentIndex + direction, 0), maxStartIndex);
    this.#scrollTo(nextIndex);
  }

  #createLoopClones() {
    if (this.slides.length < 2 || this.querySelector('[data-image-slider-clone]')) return;

    const track = this.querySelector('.image-slider__track');
    if (!track) return;

    const prepareClone = (slide, attributes) => {
      const clone = slide.cloneNode(true);
      clone.removeAttribute('data-image-slider-slide');
      clone.setAttribute('data-image-slider-clone', '');
      Object.entries(attributes).forEach(([name, value]) => clone.setAttribute(name, value));
      clone.setAttribute('aria-hidden', 'true');
      clone.removeAttribute('data-shopify-editor-block');
      clone.querySelectorAll('button').forEach((button) => {
        button.tabIndex = -1;
        button.removeAttribute('data-image-slider-open');
      });
      return clone;
    };

    const mobileFirstClone = prepareClone(this.slides[0], { 'data-image-slider-mobile-clone': 'first' });
    const mobileLastClone = prepareClone(this.slides[this.slides.length - 1], {
      'data-image-slider-mobile-clone': 'last',
    });

    const columns = Number(this.dataset.columns) || 1;
    const desktopCloneCount = this.slides.length > columns ? Math.min(columns, this.slides.length) : 0;
    const desktopFirstClones = this.slides
      .slice(0, desktopCloneCount)
      .map((slide) => prepareClone(slide, { 'data-image-slider-desktop-clone': 'first' }));
    const desktopLastClones = this.slides
      .slice(-desktopCloneCount)
      .map((slide) => prepareClone(slide, { 'data-image-slider-desktop-clone': 'last' }));

    track.prepend(mobileLastClone, ...desktopLastClones);
    track.append(...desktopFirstClones, mobileFirstClone);
  }

  #getClosestSlideIndex() {
    const scrollPosition = this.viewport.scrollLeft;
    let closestIndex = 0;
    let closestDistance = Number.POSITIVE_INFINITY;

    this.slides.forEach((slide, index) => {
      const distance = Math.abs(slide.offsetLeft - scrollPosition);
      if (distance < closestDistance) {
        closestIndex = index;
        closestDistance = distance;
      }
    });

    return closestIndex;
  }

  #handleScroll = () => {
    cancelAnimationFrame(this.#scrollFrame);
    window.clearTimeout(this.#scrollEndTimer);
    this.#scrollFrame = requestAnimationFrame(() => {
      this.#updateControls(this.#getClosestSlideIndex());
    });
    this.#scrollEndTimer = window.setTimeout(this.#settleLoop, 100);
  };

  #settleLoop = () => {
    const isDesktop = this.#isDesktop();
    const firstClone = this.querySelector(
      isDesktop ? '[data-image-slider-desktop-clone="first"]' : '[data-image-slider-mobile-clone="first"]'
    );
    const lastClone = this.querySelector(
      isDesktop ? '[data-image-slider-desktop-clone="last"]' : '[data-image-slider-mobile-clone="last"]'
    );
    const tolerance = 2;

    if (firstClone && Math.abs(this.viewport.scrollLeft - firstClone.offsetLeft) <= tolerance) {
      this.#scrollTo(0, 'instant');
      this.#updateControls(0);
    } else if (lastClone && Math.abs(this.viewport.scrollLeft - lastClone.offsetLeft) <= tolerance) {
      const columns = isDesktop ? Number(this.dataset.columns) || 1 : 1;
      const lastIndex = Math.max(this.slides.length - columns, 0);
      this.#scrollTo(lastIndex, 'instant');
      this.#updateControls(lastIndex);
    }
  };

  #updateControls(index) {
    const columns = this.#isDesktop() ? Number(this.dataset.columns) || 1 : 1;
    const maxStartIndex = Math.max(this.slides.length - columns, 0);

    this.dots.forEach((dot, dotIndex) => {
      const isCurrent = dotIndex === index;
      dot.classList.toggle('is-active', isCurrent);
      dot.setAttribute('aria-current', isCurrent ? 'true' : 'false');
    });

    const cannotMove = maxStartIndex === 0;
    if (this.previousButton) this.previousButton.disabled = cannotMove;
    if (this.nextButton) this.nextButton.disabled = cannotMove;
  }

  #startAutoplay = () => {
    this.#stopAutoplay();

    const delay = Number(this.dataset.autoplay);
    const columns = Number(this.dataset.columns) || 1;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (
      !this.#isDesktop() ||
      !delay ||
      reduceMotion ||
      this.slides.length <= columns ||
      document.hidden ||
      this.dialog?.open
    ) {
      return;
    }

    this.#autoplayTimer = window.setInterval(() => {
      const maxStartIndex = Math.max(this.slides.length - columns, 0);
      const currentIndex = Math.min(this.#getClosestSlideIndex(), maxStartIndex);
      if (currentIndex >= maxStartIndex) {
        this.#scrollToElement(this.querySelector('[data-image-slider-desktop-clone="first"]'));
      } else {
        this.#scrollTo(currentIndex + 1);
      }
    }, delay * 1000);
  };

  #stopAutoplay = () => {
    window.clearInterval(this.#autoplayTimer);
    this.#autoplayTimer = undefined;
  };

  #handleFocusOut = (event) => {
    if (!this.contains(event.relatedTarget)) this.#startAutoplay();
  };

  #handleResize = () => {
    this.#scrollTo(0, 'instant');
    this.#updateControls(0);
    this.#startAutoplay();
  };

  #handleVisibilityChange = () => {
    if (document.hidden) this.#stopAutoplay();
    else this.#startAutoplay();
  };

  #openDialog(button) {
    if (!this.dialog || !this.dialogImage) return;

    this.#stopAutoplay();
    this.dialogImage.src = button.dataset.imageSliderFullSrc;
    this.dialogImage.alt = button.dataset.imageSliderAlt || '';
    this.dialog.showModal();
  }

  #closeDialog = () => {
    this.dialog?.close();
    this.#startAutoplay();
  };

  #handleDialogClick = (event) => {
    if (event.target === this.dialog) this.#closeDialog();
  };

  #clearDialogImage = () => {
    if (this.dialogImage) this.dialogImage.removeAttribute('src');
    this.#startAutoplay();
  };
}

if (!customElements.get('image-slider')) {
  customElements.define('image-slider', ImageSlider);
}
